/**
 * Section assignment utility for the Client Workspace.
 *
 * Assigns each project to a dashboard section based on priority rules:
 * 1. Needs Attention (exclusive) — project ID is in the attentionProjectIds set
 * 2. Active — statuses: in_production, awaiting_review, revision_requested,
 *    waiting_on_client, blocked, approved
 * 3. Recently Delivered — isRecentlyDelivered === true (server-computed)
 * 4. null — visible only in "All Projects" tab (e.g., old delivered projects)
 *
 * Key design notes:
 * - "Needs Attention" is exclusive: if a project qualifies for attention AND
 *   another section, it goes to attention only.
 * - `blocked` and `approved` are explicitly included in Active (fix from review).
 * - `isRecentlyDelivered` is computed server-side (within 30 days, UTC).
 * - Projects returning null are only visible in the "All Projects" tab.
 *
 * @module sections
 * @see Requirements 8.2, 8.3
 */

import type { ClientProjectSummaryDto, ClientProjectStatus } from '../../../../types/client-workspace';

export type ProjectSection = 'attention' | 'active' | 'recently_delivered' | null;

const ACTIVE_STATUSES: ClientProjectStatus[] = [
  'in_production',
  'awaiting_review',
  'revision_requested',
  'waiting_on_client',
  'blocked',
  'approved',
];

/**
 * Assigns a project to a dashboard section based on priority rules.
 *
 * @param project - The project summary DTO to assign
 * @param attentionProjectIds - Set of project IDs that have attention items
 * @returns The section identifier, or null if the project belongs only in "All Projects"
 */
export function assignProjectToSection(
  project: ClientProjectSummaryDto,
  attentionProjectIds: Set<string>,
): ProjectSection {
  // Priority 1: Needs Attention (exclusive)
  if (attentionProjectIds.has(project.id)) {
    return 'attention';
  }

  // Priority 2: Active statuses
  if (ACTIVE_STATUSES.includes(project.status)) {
    return 'active';
  }

  // Priority 3: Recently Delivered (server-computed flag)
  if (project.isRecentlyDelivered) {
    return 'recently_delivered';
  }

  // Priority 4: No section — visible only in "All Projects" tab
  // (e.g., delivered projects older than 30 days)
  return null;
}
